import type { ApiPaginatedResponse } from '@_/types';
import type { ClientContext, ClientPromiseResult, ClientRest } from '@orpc/client';
import type { GeneralUtils, ProcedureUtils } from '@orpc/tanstack-query';
import type { InfiniteData, QueryKey, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { fetchAllPages, isApiError } from './apiUtil.ts';

export function retryOnlyIfStatus500(failureCount: number, error: unknown): boolean {
  return isApiError(error) && error.status >= 500 && failureCount < 3;
}

export function getItemsInInfiniteQuery<Item>(data?: InfiniteData<ApiPaginatedResponse<Item[]>>): {
  items: Item[];
  totalItems: number;
  nextCursor?: string | null;
} {
  const { pages } = data || {};

  let items: Item[] = [];
  let totalItems = 0;
  let nextCursor;

  if (pages instanceof Array && pages.length) {
    items = pages.map(p => p.data).flat() || [];
    const lastPagination = pages[pages.length - 1].pagination;
    totalItems = lastPagination.totalItems;
    nextCursor = lastPagination.nextCursor;
  }

  return {
    items,
    totalItems,
    nextCursor
  };
}

export function getReactQueryOptions<TClientContext extends ClientContext, TInput, TOutput, TError>(
  endpoint: ProcedureUtils<TClientContext, TInput, TOutput, TError> & GeneralUtils<TInput>,
  ...endpointArgs: ClientRest<TClientContext, TInput>
): {
  queryKey: QueryKey;
  queryFn: () => ClientPromiseResult<TOutput, TError>;
} {
  return {
    queryKey: endpoint.key({ type: 'query', input: endpointArgs[0] as never }),
    queryFn: async () => endpoint.call(...endpointArgs)
  };
}

export function getInfiniteReactQueryOptions<
  TClientContext extends ClientContext,
  TInput extends { query?: { afterCursor?: string } },
  TOutput extends ApiPaginatedResponse<unknown[]>,
  TError
>(
  endpoint: ProcedureUtils<TClientContext, TInput, TOutput, TError> & GeneralUtils<TInput>,
  ...endpointArgs: ClientRest<TClientContext, TInput>
): Pick<
  UseInfiniteQueryOptions<TOutput>,
  'getNextPageParam' | 'getPreviousPageParam' | 'initialPageParam' | 'queryFn' | 'queryKey'
> {
  return {
    queryKey: endpoint.key({
      type: 'infinite',
      input: endpointArgs[0] as never
    }),
    queryFn: async context => {
      const args = [
        {
          ...endpointArgs[0],
          query: {
            ...endpointArgs[0]?.query,
            afterCursor: context.pageParam || undefined
          }
        },
        ...(endpointArgs?.slice(1) || [])
      ] as never as ClientRest<TClientContext, TInput>;

      return endpoint.call(...args);
    },
    getNextPageParam: lastPage => lastPage.pagination.nextCursor,
    getPreviousPageParam: firstPage => firstPage.pagination.previousCursor,
    initialPageParam: null
  };
}

export function getFetchAllReactQueryOptions<
  TClientContext extends ClientContext,
  TInput extends { query?: { afterCursor?: string } },
  TOutput extends ApiPaginatedResponse<unknown[]>,
  TError
>(
  endpoint: ProcedureUtils<TClientContext, TInput, TOutput, TError> & GeneralUtils<TInput>,
  ...endpointArgs: ClientRest<TClientContext, TInput>
): {
  queryKey: QueryKey;
  queryFn: () => Promise<TOutput extends ApiPaginatedResponse<infer TData> ? TData : never>;
} {
  return {
    queryKey: [...endpoint.key({ input: endpointArgs[0] as never }), 'fetch-all'],
    queryFn: (async () => {
      return fetchAllPages(async ({ nextCursor }) => {
        const args = [
          {
            ...endpointArgs[0],
            query: { ...endpointArgs[0]?.query, afterCursor: nextCursor }
          },
          ...(endpointArgs?.slice(1) || [])
        ] as never as ClientRest<TClientContext, TInput>;

        return endpoint.call(...args);
      });
    }) as () => Promise<never>
  };
}
